import { Box, Button, Card, Grid, Typography, Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress, Chip } from '@mui/material'
import React, { useEffect, useState } from 'react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../navbar';
import { getPatientMedicalHistory } from '../../service/patienthistory';
import { getClinicalObservation } from '../../service/clinicalobservation';
import { QUESTIONNAIRES, getQuestionnaire, getSeverity, getAssessmentsByCategory } from '../Assessments/questionnaires';
import { getAssessment } from '../../service/customerassessment';


const ViewCaseHistory = () => {
    const navigate = useNavigate();
    const { patientId, appointmentId } = useParams();
    const [appointmentData, setAppointmentData] = useState("")
    const [isNewCase, setIsNewCase] = useState(true);
    const [currentQuestionnaire, setCurrentQuestionnaire] = useState(null);
    const [questionnaireAnswers, setQuestionnaireAnswers] = useState({});
    const [questionnaireScores, setQuestionnaireScores] = useState({});
    const [showQuestionnaireModal, setShowQuestionnaireModal] = useState(false);
    const [assessmentData, setAssessmentData] = useState({});
    const [loadingAssessments, setLoadingAssessments] = useState(false);
    

    const [formData, setFormData] = useState({
        chief_complaint: '',
        presenting_issue: '',
        medical_history: '',
        current_medications: '',
        allergies: '',
        family_history: '',
        social_history: '',
        mental_health_history: '',
        substance_use: '',
        previous_treatments: '',
        current_stressors: '',
        coping_mechanisms: '',
        trauma_history: '',
        patient_strengths: '',
        sessionObservations: '',
        clinicalImpressions: '',
        therapistNotes: '',
        workingDiagnosis: '',
        recommendedIntervention: '',
        recommendedFrequency: '',
        treatmentGoals: '',
        triageLevel: '',
        sessionSummary: '',
        interventionsUsed: '',
        homeworkAssigned: '',
        planForNextSession: '',
        selectedAssessments: [],
        assessment_keys: [],
    });

    const resolvedPatientId = String(patientId || '').trim();

    useEffect(() => {
        if (!appointmentId) return;

        const loadAppointment = async () => {
            try {
                const res = await fetch(`/api/v1/appointments/${appointmentId}`);

                if (!res.ok) {
                    throw new Error("API failed");
                }

                const text = await res.text();
                const data = text ? JSON.parse(text) : {};
                setAppointmentData(data)
                setIsNewCase(Boolean(data?.is_new_case));
            } catch (err) {
                console.error("Failed to load appointment", err);
                setIsNewCase(false);
            }
        };
        loadAppointment();
    }, [appointmentId]);

    useEffect(() => {

        const loadMedicalHistory = async () => {

            try {
                const data = await getPatientMedicalHistory(patientId, appointmentId);
                if (data && typeof data === 'object') {
                    setFormData(prev => ({
                        ...prev,
                        chief_complaint: data.chief_complaint || '',
                        presenting_issue: data.presenting_issue || '',
                        medical_history: data.medical_history || '',
                        current_medications: data.current_medications || '',
                        allergies: data.allergies || '',
                        family_history: data.family_history || '',
                        social_history: data.social_history || '',
                        mental_health_history: data.mental_health_history || '',
                        substance_use: data.substance_use || '',
                        previous_treatments: data.previous_treatments || '',
                        current_stressors: data.current_stressors || '',
                        coping_mechanisms: data.coping_mechanisms || '',
                        trauma_history: data.trauma_history || '',
                        patient_strengths: data.patient_strengths || ''
                    }));
                } else {
                    console.warn('[CaseHistory] GET medical history returned empty/invalid payload', data);
                }
            } catch (err) {
                console.error('[CaseHistory] GET medical history failed', err);
            }
        };

        loadMedicalHistory();

    }, [patientId, appointmentId]); // Add appointmentId to the dependency array

    const medicalHistoryFields = [
        { label: "Chief Complaint", key: "chief_complaint" },
        { label: "Presenting Issue", key: "presenting_issue" },
        { label: "Medical History", key: "medical_history" },
        { label: "Current Medications", key: "current_medications" },
        { label: "Allergies", key: "allergies" },
        { label: "Family History", key: "family_history" },
        { label: "Social History", key: "social_history" },
        { label: "Mental Health History", key: "mental_health_history" },
        { label: "Substance Use", key: "substance_use" },
        { label: "Previous Treatments", key: "previous_treatments" },
        { label: "Current Stressors", key: "current_stressors" },
        { label: "Coping Mechanisms", key: "coping_mechanisms" },
        { label: "Trauma History", key: "trauma_history" },
        { label: "Patient Strengths", key: "patient_strengths" }
    ];

    const clinicalObservationFields = [
        { label: "Session Observations", key: "sessionObservations" },
        { label: "Clinical Impressions", key: "clinicalImpressions" },
        { label: "Therapist Notes", key: "therapistNotes" },
        { label: "Working Diagnosis", key: "workingDiagnosis" },
        { label: "Recommended Intervention", key: "recommendedIntervention" },
        { label: "Recommended Frequency", key: "recommendedFrequency" },
        { label: "Therapeutic Goals", key: "treatmentGoals" },
        { label: "Triage Level", key: "triageLevel" },
        { label: "Session Summary", key: "sessionSummary" },
        { label: "Interventions Used", key: "interventionsUsed" },
        { label: "Homework Assigned", key: "homeworkAssigned" },
        { label: "Plan For Next Session", key: "planForNextSession" },
        { label: "Treatment Objectives", key: "treatmentObjectives" },
        { label: "Additional Notes", key: "additionalNotes" },
        { label: "Follow-up Plan", key: "followUpPlan" },


    ];

    const matchedAssessments = React.useMemo(() => {
        if (!Array.isArray(formData.assessment_keys)) return [];

        return formData.assessment_keys
            .map(key => {
                const q = QUESTIONNAIRES[key];
                if (!q) return null;
                return { ...q, key };  // <--- add the key here
            })
            .filter(Boolean);
    }, [formData.assessment_keys]);

    useEffect(() => {
        if (!appointmentId) return;

        const loadClinicalObservation = async () => {
            try {
                const data = await getClinicalObservation(appointmentId);

                if (!data || typeof data !== 'object') return;

                setFormData(prev => ({
                    ...prev,
                    sessionObservations: data.session_observations || '',
                    clinicalImpressions: data.clinical_impressions || '',
                    therapistNotes: data.therapist_notes || '',
                    workingDiagnosis: data.working_diagnosis || '',
                    recommendedIntervention: data.recommended_intervention || '',
                    recommendedFrequency: data.recommended_frequency || '',
                    treatmentGoals: data.treatment_goals || '',
                    //  3Additional fields
                    treatmentObjectives: data.treatment_objectives || '',
                    additionalNotes: data.additional_notes || '',
                    followUpPlan: data.follow_up_plan || '',
                    triageLevel: data.triage_level || '',
                    sessionSummary: data.session_summary || '',
                    interventionsUsed: data.interventions_used || '',
                    homeworkAssigned: data.homework_assigned || '',
                    planForNextSession: data.plan_for_next_session || '',
                    assessment_keys: data.assessment_keys || []
                }));
            } catch (err) {
                console.error("Failed to load clinical observations", err);
            }
        };

        loadClinicalObservation();
    }, [appointmentId]);

    // Fetch assessment data for all matched assessments
    useEffect(() => {
        if (!appointmentId || matchedAssessments.length === 0) return;

        const fetchAssessmentData = async () => {
            setLoadingAssessments(true);
            const data = {};
            
            try {
                await Promise.all(
                    matchedAssessments.map(async (assessment) => {
                        try {
                            const result = await getAssessment(appointmentId, assessment.key);
                            if (result) {
                                data[assessment.key] = result;
                            }
                        } catch (error) {
                            console.log(`No assessment data found for ${assessment.key}`);
                        }
                    })
                );
                setAssessmentData(data);
            } catch (error) {
                console.error('Error fetching assessment data:', error);
            } finally {
                setLoadingAssessments(false);
            }
        };

        fetchAssessmentData();
    }, [appointmentId, matchedAssessments]);

    // Helper function to compute score from assessment answers
    const computeScore = (assessmentKey, answers) => {
        const questionnaire = getQuestionnaire(assessmentKey);
        if (!questionnaire || !answers) return null;

        let totalScore = 0;
        Object.entries(answers).forEach(([questionId, optionIndex]) => {
            const qId = Number(questionId);
            const question = questionnaire.questions.find(q => q.id === qId);
            if (question?.options?.[optionIndex]) {
                totalScore += question.options[optionIndex].score || 0;
            }
        });

        return totalScore;
    };

    // Helper function to format date
    const formatDate = (dateString) => {
        if (!dateString) return null;
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            });
        } catch {
            return dateString;
        }
    };

    // Helper function to get progress percentage
    const getProgressPercentage = (score, maxScore) => {
        if (!score || !maxScore) return 0;
        return Math.min((score / maxScore) * 100, 100);
    };

    const startAssessment = async (assessmentKey) => {
        const questionnaire = getQuestionnaire(assessmentKey);
        if (!questionnaire) {
            console.error("No questionnaire found for key", assessmentKey);
            return;
        }

        setCurrentQuestionnaire(questionnaire);  // store full object
        setQuestionnaireAnswers({});
        setQuestionnaireScores({});

        if (appointmentId) {
            try {
                const existing = await getAssessment(appointmentId, assessmentKey);

                if (existing?.answers) {
                    const answers = {};
                    const scores = {};

                    Object.entries(existing.answers).forEach(([questionId, optionIndex]) => {
                        const qId = Number(questionId);
                        answers[qId] = optionIndex;

                        const question = questionnaire.questions.find(q => q.id === qId);

                        if (question?.options?.[optionIndex]) {
                            scores[qId] = question.options[optionIndex].score || 0;
                        }
                    });

                    setQuestionnaireAnswers(answers);
                    setQuestionnaireScores(scores);
                }
            } catch {
                console.log("No previous assessment found");
            }
        }

        setShowQuestionnaireModal(true);
    };

    return (
        <Box sx={{
            minHeight: '100vh', background: 'linear-gradient(135deg, #f8fffe 0%, #f5f3ff 100%)'
        }}>
            <Navbar />

            {/* Header */}
            <Box sx={{
                bgcolor: 'white', borderBottom: '1px solid #e5e7eb', background: 'linear-gradient(135deg, #f8fffe 0%, #f5f3ff 100%)'
            }}>
                <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: { xs: 2, sm: 3 } }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/dashboard')}
                        sx={{ mb: 2, color: '#4C41FF', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#f0f0ff' } }}
                    >
                        Back to Appointments
                    </Button>
                    <Card sx={{ bgcolor: '#fff7ed', border: '2px solid #fed7aa', boxShadow: 'none', p: { xs: 2, sm: 3 } }}>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 2, sm: 3 } }}>
                            <Box sx={{
                                width: { xs: 48, sm: 64 },
                                height: { xs: 48, sm: 64 },
                                borderRadius: 2,
                                bgcolor: '#fb923c',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 700,
                                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                                boxShadow: '0 4px 6px rgba(251, 146, 60, 0.3)'
                            }}>
                                {resolvedPatientId?.slice(-2) || 'PT'}

                            </Box>
                            <Box sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 1, sm: 2 }, mb: 1 }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.25rem', sm: '1.5rem' }, color: '#1a1a1a' }}>
                                        {appointmentData?.customer_name}
                                    </Typography>
                                    <Box
                                        sx={{
                                            px: 2,
                                            py: 0.5,
                                            bgcolor: isNewCase ? '#22c55e' : '#fb923c',
                                            color: 'white',
                                            borderRadius: 1.5,
                                            fontSize: '0.75rem',
                                            fontWeight: 700
                                        }}
                                    >
                                        {isNewCase ? 'NEW' : 'RETURNING'}
                                    </Box>

                                </Box>
                                <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, color: '#92400e', fontWeight: 500, wordBreak: 'break-word' }}>
                                    ID: {resolvedPatientId || 'N/A'}
                                </Typography>
                            </Box>
                            <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, width: { xs: '100%', sm: 'auto' } }}>
                                <Typography sx={{ fontSize: '0.75rem', color: '#92400e', fontWeight: 600, mb: 0.5 }}>
                                    Booking Date
                                </Typography>
                                <Typography sx={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                    {appointmentData?.created_at}
                                </Typography>
                            </Box>
                        </Box>
                    </Card>

                    {/* Medical History Section */}
                    <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: 4 }}>
                        <Card
                            sx={{
                                borderRadius: 3,
                                p: { xs: 2.5, sm: 3 },
                                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                                bgcolor: '#ffffff',
                                borderLeft: '5px solid #4a90e2'
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '1.2rem',
                                    fontWeight: 700,
                                    mb: 3,
                                    color: '#1f2937',
                                    letterSpacing: '0.3px'
                                }}
                            >
                                Case History
                            </Typography>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        sm: '1fr 1fr'
                                    },
                                    gap: 3
                                }}
                            >
                                {medicalHistoryFields.map(({ label, key }) => (
                                    <Box
                                        key={key}
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            bgcolor: '#f9fafb',
                                            border: '1px solid #e5e7eb',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                bgcolor: '#f3f4f6'
                                            }
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '0.7rem',
                                                fontWeight: 700,
                                                color: '#6b7280',
                                                mb: 0.75,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.6px'
                                            }}
                                        >
                                            {label}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: '0.95rem',
                                                fontWeight: 500,
                                                color: '#111827',
                                                whiteSpace: 'pre-line',
                                                lineHeight: 1.6
                                            }}
                                        >
                                            {formData[key] || '—'}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Card>
                    </Box>

                    {/* Clinical Observations Section */}
                    <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, pb: 4 }}>
                        <Card
                            sx={{
                                borderRadius: 3,
                                p: { xs: 2.5, sm: 3 },
                                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                                bgcolor: '#ffffff',
                                borderLeft: '5px solid #22c55e'
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '1.2rem',
                                    fontWeight: 700,
                                    mb: 3,
                                    color: '#1f2937',
                                    letterSpacing: '0.3px'
                                }}
                            >
                                Clinical Observations
                            </Typography>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        sm: '1fr 1fr'
                                    },
                                    gap: 3
                                }}
                            >
                                {clinicalObservationFields.map(({ label, key }) => (
                                    <Box
                                        key={key}
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            bgcolor: '#f9fafb',
                                            border: '1px solid #e5e7eb',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '0.7rem',
                                                fontWeight: 700,
                                                color: '#6b7280',
                                                mb: 0.75,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.6px'
                                            }}
                                        >
                                            {label}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: '0.95rem',
                                                fontWeight: 500,
                                                color: '#111827',
                                                whiteSpace: 'pre-line',
                                                lineHeight: 1.6
                                            }}
                                        >
                                            {formData[key] || '—'}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Card>
                    </Box>

                 

                    <Box sx={{ mt: 4, mb: 4 }}>
                       
                        
                        {loadingAssessments ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <Typography color="text.secondary">Loading assessment data...</Typography>
                            </Box>
                        ) : (
                            <Grid container spacing={2}>
                            {matchedAssessments.map(q => {
                                const assessment = assessmentData[q.key];
                                const score = assessment ? computeScore(q.key, assessment.answers) : null;
                                const severity = score !== null ? getSeverity(q.key, score) : null;
                                const progressPercentage = getProgressPercentage(score, q.maxScore);
                                const hasData = assessment && score !== null;
                                
                                // Determine border color based on severity
                                const getBorderColor = () => {
                                    if (!hasData) return '#e5e7eb';
                                    switch (severity?.color) {
                                        case 'success': return '#22c55e';
                                        case 'warning': return '#facc15';
                                        case 'error': return '#ef4444';
                                        default: return '#e5e7eb';
                                    }
                                };

                                return (
                                    <Grid item xs={12} sm={6} md={4} key={q.key}>
                                        <Card
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                startAssessment(q.key);
                                            }}
                                            sx={{
                                                p: 1,
                                                cursor: "pointer",
                                                border: `2px solid ${getBorderColor()}`,
                                                borderRadius: 2,
                                                transition: "all 0.2s",
                                                bgcolor: hasData ? (severity?.color === 'error' ? '#fef2f2' : 
                                                                      severity?.color === 'warning' ? '#fffbeb' : 
                                                                      severity?.color === 'success' ? '#f0fdf4' : '#ffffff') : '#ffffff',
                                                "&:hover": {
                                                    borderColor: "#4f46e5",
                                                    boxShadow: 2,
                                                    transform: 'translateY(-1px)'
                                                }
                                            }}
                                        >
                                            {/* Header with icon, category and date */}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                                <Typography fontSize="1.5rem">{q.icon}</Typography>
                                                <Box sx={{ textAlign: 'right' }}>
                                                    <Chip
                                                        label={q.category}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: q.category === 'anxiety' ? '#fef3c7' : 
                                                                     q.category === 'depression' ? '#dbeafe' : '#f3e8ff',
                                                            color: q.category === 'anxiety' ? '#92400e' : 
                                                                   q.category === 'depression' ? '#1e40af' : '#6b21a8',
                                                            fontSize: '0.7rem',
                                                            fontWeight: 600,
                                                            mb: 0.25
                                                        }}
                                                    />
                                                    {assessment && assessment.created_at && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                            {formatDate(assessment.created_at)}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>

                                            {/* Assessment name */}
                                            <Typography fontWeight={600} fontSize="0.8rem" sx={{ mb: 0.25, textAlign: 'center' }}>
                                                {q.shortName}
                                            </Typography>
                                            
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, textAlign: 'center' }}>
                                                {q.name}
                                            </Typography>

                                            {/* Score and Severity */}
                                            {hasData ? (
                                                <Box sx={{ mb: 1 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                                        <Typography variant="h6" fontWeight={700} color="text.primary">
                                                            {score}/{q.maxScore}
                                                        </Typography>
                                                        {severity && (
                                                            <Chip
                                                                label={severity.level}
                                                                size="small"
                                                                color={severity.color}
                                                                sx={{ fontSize: '0.7rem', fontWeight: 600 }}
                                                            />
                                                        )}
                                                    </Box>
                                                    
                                                    {/* Progress Bar */}
                                                    <Box sx={{ mb: 1 }}>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={progressPercentage}
                                                            sx={{
                                                                height: 8,
                                                                borderRadius: 4,
                                                                bgcolor: '#e5e7eb',
                                                                '& .MuiLinearProgress-bar': {
                                                                    borderRadius: 4,
                                                                    bgcolor: severity?.color === 'error' ? '#ef4444' :
                                                                             severity?.color === 'warning' ? '#f59e0b' :
                                                                             severity?.color === 'success' ? '#22c55e' : '#6b7280'
                                                                }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                            ) : (
                                                <Box sx={{ mb: 2, textAlign: 'center', py: 2 }}>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                        Not completed
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {q.questions.length} items
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                        )}
                    </Box>


                </Box>

            </Box>

            <Dialog
                open={showQuestionnaireModal}
                onClose={() => setShowQuestionnaireModal(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: 'hidden',
                    },
                }}
            >
                {/* Header */}
                <Box sx={{ bgcolor: '#4f46e5', py: 2, px: 3 }}>
                    <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.25rem' }}>
                        {currentQuestionnaire?.type || 'Assessment'}
                    </Typography>
                </Box>

                <DialogContent dividers sx={{ bgcolor: '#f9fafb', py: 3 }}>
                    {currentQuestionnaire ? currentQuestionnaire.questions.map(q => (
                        <Card
                            key={q.id}
                            variant="outlined"
                            sx={{
                                mb: 2,
                                p: 2,
                                borderRadius: 2,
                                bgcolor: 'white',
                                boxShadow: 1,
                            }}
                        >
                            <Typography fontWeight={600} sx={{ mb: 1, color: '#1f2937' }}>
                                {q.text}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {q.options.map((opt, idx) => (
                                    <Button
                                        key={idx}
                                        variant={questionnaireAnswers[q.id] === idx ? 'contained' : 'outlined'}
                                        onClick={(e) => e.preventDefault()} // prevent clicks
                                        disableRipple // remove ripple effect
                                        sx={{
                                            flex: { xs: '1 1 100%', sm: '1 1 auto' },
                                            minWidth: 120,
                                            textTransform: 'none',
                                            bgcolor: questionnaireAnswers[q.id] === idx ? '#4f46e5' : 'white',
                                            color: questionnaireAnswers[q.id] === idx ? 'white' : '#111827',
                                            cursor: 'default', // indicate non-clickable
                                            borderColor: '#e5e7eb',
                                            '&:hover': { bgcolor: questionnaireAnswers[q.id] === idx ? '#4f46e5' : 'white' }, // no hover change
                                        }}
                                    >
                                        {opt.text}
                                    </Button>
                                ))}
                            </Box>


                        </Card>
                    )) : (
                        <Typography>Loading...</Typography>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => setShowQuestionnaireModal(false)}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>


        </Box>
    )
}

export default ViewCaseHistory
